import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { filterAstToText, parseFilterExpr, type FilterNode } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { filterExprToHuman } from './filter-expr-human';

/**
 * Puces de DÉCOMPOSITION du FILTRE AVANCÉ actif (`state.expr`). Deux rendus :
 *  - expression dont la racine est un OU de premier niveau → UNE puce par terme, reliées
 *    par « ou », chacune EFFAÇABLE (retirer un terme reconstruit l'expression sans lui) ;
 *  - sinon (terme unique, ET, négation, groupes imbriqués) → une seule puce « Expression
 *    avancée » portant l'ÉCHO en langage naturel (title), effaçable (retire tout le filtre).
 *
 * Chaque puce porte une icône + un texte (jamais la couleur seule). Une expression qui ne
 * parse pas reste affichée en une puce générique effaçable (l'API l'aurait de toute façon
 * rejetée, mais on laisse l'utilisateur la retirer proprement).
 */

interface ExprFilterChipsProps {
  /** Expression avancée courante (`state.expr`). Vide/absente ⇒ aucun rendu. */
  expr: string | undefined;
  /** Retire TOUT le filtre avancé (retour à l'état sans expression). */
  onClearExpr: () => void;
  /** Remplace l'expression (après retrait d'un terme du OU de premier niveau). */
  onChangeExpr: (expr: string) => void;
}

export function ExprFilterChips({
  expr,
  onClearExpr,
  onChangeExpr,
}: ExprFilterChipsProps): React.JSX.Element | null {
  const { t } = useI18n();
  if (expr === undefined || expr.trim() === '') return null;

  const parsed = parseFilterExpr(expr);

  // Expression invalide : puce générique unique (effaçable).
  if (!parsed.ok) {
    return (
      <ChipRow label={t('exprChips.label')}>
        <Chip
          text={t('exprChips.advanced')}
          title={expr}
          removeLabel={t('exprChips.remove')}
          onRemove={onClearExpr}
        />
      </ChipRow>
    );
  }

  const ast = parsed.ast;

  // Racine = OU de premier niveau → une puce par terme, chacune effaçable.
  if (ast.kind === 'or') {
    const terms = ast.children;
    return (
      <ChipRow label={t('exprChips.label')}>
        {terms.map((term, index) => (
          <span key={index} className="flex items-center gap-2">
            {index > 0 && <span className="text-xs text-muted">{t('filterExpr.or')}</span>}
            <Chip
              text={filterExprToHuman(term, t)}
              title={filterExprToHuman(term, t)}
              removeLabel={t('exprChips.remove')}
              onRemove={() => removeTerm(terms, index, onChangeExpr, onClearExpr)}
            />
          </span>
        ))}
      </ChipRow>
    );
  }

  // Sinon : une seule puce « Expression avancée » avec l'écho complet.
  return (
    <ChipRow label={t('exprChips.label')}>
      <Chip
        text={t('exprChips.advanced')}
        title={filterExprToHuman(ast, t)}
        removeLabel={t('exprChips.remove')}
        onRemove={onClearExpr}
      />
    </ChipRow>
  );
}

/** Retire le terme `index` d'un OU : reconstruit l'expression (ou l'efface s'il ne reste rien). */
function removeTerm(
  terms: FilterNode[],
  index: number,
  onChangeExpr: (expr: string) => void,
  onClearExpr: () => void,
): void {
  const remaining = terms.filter((_, i) => i !== index);
  if (remaining.length === 0) {
    onClearExpr();
    return;
  }
  const next: FilterNode =
    remaining.length === 1 ? remaining[0]! : { kind: 'or', children: remaining };
  onChangeExpr(filterAstToText(next));
}

function ChipRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div role="group" aria-label={label} className="flex flex-wrap items-center gap-2">
      {children}
    </div>
  );
}

function Chip({
  text,
  title,
  removeLabel,
  onRemove,
}: {
  text: string;
  title: string;
  removeLabel: string;
  onRemove: () => void;
}): React.JSX.Element {
  return (
    <span
      title={title}
      className="flex items-center gap-1.5 rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-fg"
    >
      <FunnelIcon className="h-4 w-4 shrink-0 text-muted" aria-hidden="true" />
      <span className="max-w-[16rem] truncate">{text}</span>
      <button
        type="button"
        onClick={onRemove}
        title={removeLabel}
        aria-label={removeLabel}
        className="rounded-md p-0.5 text-muted hover:bg-surface hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <XMarkIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
      </button>
    </span>
  );
}
